import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Image, Paper, Text } from '@mantine/core'
import { Toast } from 'antd-mobile'
import { getDictByKey } from '../../api/dictionaryService'
import { useLotteryData } from '../hooks'
import { Countdown } from './components/CountdownComponents'
import { ProgressBar } from './components/ProgressComponents'
import { CurrentPoints, TargetPoints } from './components/PointsComponents'
import ContactService from '../components/ContactService'

const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24
const MILLISECONDS_PER_HOUR = 1000 * 60 * 60

const GrandPrize: React.FC = () => {
  const [targetPoints, setTargetPoints] = useState('0')
  const [countdownDays, setCountdownDays] = useState('0')
  const [countdownHours, setCountdownHours] = useState('0')
  const { currentMerchant, customerServiceUrl } = useLotteryData()

  const handleClick = useCallback(() => {
    Toast.show({
      content: (
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>🚗✨</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#FF5A5A' }}>年终盛典敬请期待！</div>
        </div>
      ),
      position: 'center',
      duration: 2500,
    })
  }, [])

  const calculateCountdown = useCallback((endTimeString: string) => {
    const endDate = new Date(endTimeString)
    const now = new Date()
    const diff = Math.max(0, endDate.getTime() - now.getTime())

    const days = Math.floor(diff / MILLISECONDS_PER_DAY)
    const hours = Math.floor((diff % MILLISECONDS_PER_DAY) / MILLISECONDS_PER_HOUR)

    return { days: String(days), hours: String(hours) }
  }, [])

  const salesProgressPercentage = useMemo(() => {
    if (!currentMerchant || !targetPoints) return 0
    const current = Number(currentMerchant.totalPoints) || 0
    const target = Number(targetPoints) || 1
    return Math.min((current / target) * 100, 100)
  }, [currentMerchant, targetPoints])

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [points, endTime] = await Promise.all([
          getDictByKey('key_merchant_grand_prize_target_points'),
          getDictByKey('key_merchant_grand_prize_end_time'),
        ])

        setTargetPoints(points)
        const countdown = calculateCountdown(endTime)
        setCountdownDays(countdown.days)
        setCountdownHours(countdown.hours)
      } catch (error) {
        console.error('Failed to fetch grand prize data:', error)
      }
    }

    fetchInitialData()
  }, [calculateCountdown])

  useEffect(() => {
    const updateCountdown = () => {
      getDictByKey('key_merchant_grand_prize_end_time')
        .then((endTime) => {
          const countdown = calculateCountdown(endTime)
          setCountdownDays(countdown.days)
          setCountdownHours(countdown.hours)
        })
        .catch(console.error)
    }

    const interval = setInterval(updateCountdown, 60000)
    return () => clearInterval(interval)
  }, [calculateCountdown])

  return (
    <Paper
      shadow="lg"
      radius={24}
      withBorder
      onClick={handleClick}
      style={{
        background: 'linear-gradient(135deg, #FFE2B3 0%, #FDE6E9 50%, #FFF6DB 100%)',
        border: 'none',
        position: 'relative',
        overflow: 'visible',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          background: 'linear-gradient(135deg, #FF5A5A, #FF8E8E)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: 12,
          fontSize: 11,
          fontWeight: 600,
          zIndex: 2,
          boxShadow: '0 2px 8px rgba(255, 90, 90, 0.3)',
        }}
      >
        点击了解
      </div>

      <Image
        src="/su7.png"
        style={{
          position: 'absolute',
          top: -60,
          right: -30,
          width: 240,
          height: 'auto',
          zIndex: 1,
          filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.2))',
        }}
      />

      <Box style={{ padding: 16 }}>
        <Box style={{ marginBottom: 20, marginTop: 12 }}>
          <Text
            fw={900}
            style={{
              fontSize: 24,
              color: '#3A2323',
              lineHeight: 1.2,
              textShadow: '0 2px 4px rgba(58, 35, 35, 0.2)',
            }}
          >
            年终盛典
          </Text>
          <Text
            fw={900}
            mt={8}
            style={{
              fontSize: 24,
              color: '#A13A1A',
              lineHeight: 1.2,
              textShadow: '0 2px 4px rgba(161, 58, 26, 0.3)',
            }}
          >
            冲刺赢小米SU7 🚗
          </Text>
        </Box>

        <TargetPoints points={targetPoints} />
        <CurrentPoints points={currentMerchant?.totalPoints || 0} />
        <ProgressBar percentage={salesProgressPercentage} />
        <Countdown days={countdownDays} hours={countdownHours} />

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <ContactService
            url={customerServiceUrl}
            size="small"
            text="活动咨询"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              color: '#07C160',
              border: '1px solid #07C160',
              fontSize: '12px',
            }}
          />
        </div>
      </Box>
    </Paper>
  )
}

export default GrandPrize
